import { useMemo, useState, useEffect } from 'react';
import {
  Drawer, Box, Stack, Typography, Chip, IconButton, Divider, Button, Tooltip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Collapse, alpha, useTheme,
  TextField, InputAdornment,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SettingsEthernetRoundedIcon from '@mui/icons-material/SettingsEthernetRounded'
import { CHECK_DESCRIPTIONS } from './checkDescriptions';
import { standardsForCheck } from './complianceMappings';
import { unstable_defaultSxConfig } from '@mui/system';

// Map an audit PASS/WARN/FAIL string to a colour + MUI chip colour.
const resultMeta = (res) => {
  const R = (res == null ? '' : String(res)).trim().toUpperCase();
  if (R === 'PASS') return { color: 'success', label: 'PASS' };
  if (R === 'WARN' || R === 'WARNING') return { color: 'warning', label: R === 'WARNING' ? 'WARNING' : 'WARN' };
  if (R === 'FAIL') return { color: 'error', label: 'FAIL' };
  if (R === 'PENDING') return { color: 'default', label: 'PENDING' };
  if (R === 'NOTAPPLICABLE') return { color: 'default', label: 'N/A' };
  return { color: 'default', label: res ? String(res) : '—' };
};

const rowBucket = (b) => {
  if (b.suppressed) return 'suppressed'
  const R = (b.result == null ? '' : String(b.result)).trim().toUpperCase();
  if (R === 'PASS') return 'pass';
  if (R === 'WARN' || R === 'WARNING') return 'warn';
  if (R === 'FAIL') return 'fail';
  return 'other';
};

const STATUS_ICON = {
  pass:     { Icon: CheckCircleRoundedIcon,          color: 'success.main' },
  fail:     { Icon: CancelRoundedIcon,               color: 'error.main' },
  warn:     { Icon: WarningAmberRoundedIcon,         color: 'warning.main' },
  excluded: { Icon: RemoveCircleOutlineRoundedIcon,  color: 'text.disabled' },
  nodata:   { Icon: HourglassEmptyRoundedIcon,       color: 'text.disabled' },
  manual:   { Icon: RadioButtonUncheckedRoundedIcon, color: 'text.disabled' },
  pending:  { Icon: ConstructionRoundedIcon,         color: 'text.disabled' },
  notapplicable: { Icon: HorizontalRuleRoundedIcon,  color: 'text.disabled' },
};

function Row({ label, children }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{children ?? '—'}</Typography>
    </Stack>
  );
}

// Render a raw field value; epoch-ms timestamps get a human date appended.
function fmtRawVal(key, v) {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  if (/last.?seen|date|time/i.test(key) && typeof v === 'number' && v > 1e12) {
    try { return `${v}  (${new Date(v).toISOString().slice(0, 16).replace('T', ' ')} UTC)`; }
    catch { return String(v); }
  }
  return String(v);
}

// One result row + a collapsible panel showing every raw field for that target.
function ResultRow({ b, canSuppress, onSuppress, onUnsuppress, canSetCapacity, onSetCapacity }) {
  const [open, setOpen] = useState(false);
  const m = resultMeta(b.result);
  const rawEntries = b.raw ? Object.entries(b.raw) : [];
  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: rawEntries.length ? 'none' : undefined }, opacity: b.suppressed ? 0.5 : 1 }}>
        <TableCell sx={{ width: 28, p: 0 }}>
          {rawEntries.length > 0 && (
            <IconButton size="small" onClick={() => setOpen(o => !o)}>
              {open ? <KeyboardArrowDownRoundedIcon fontSize="small" /> : <KeyboardArrowRightRoundedIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ maxWidth: 150 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: 'break-word', textDecoration: b.suppressed ? 'line-through' : 'none' }}>{b.target}</Typography>
          {b.date && <Typography variant="caption" color="text.disabled" display="block">{String(b.date).slice(0, 10)}</Typography>}
        </TableCell>
        <TableCell>
          {b.suppressed
            ? <Chip size="small" variant="outlined" label="suppressed" sx={{ height: 18 }} />
            : <Chip size="small" color={m.color} variant="outlined" label={m.label} sx={{ height: 18 }} />}
        </TableCell>
        <TableCell><Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{b.message || '—'}</Typography></TableCell>
        <TableCell sx={{ width: 64, p: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {canSetCapacity && (
            <Tooltip title = {b.capacity ? `Capacity ${b.capacity.download_mbps}/${b.capacity.upload_mbps} Mbps -- edit` :'Set circuit capacity'}>
              <IconButton size="small" onClick={() => onSetCapacity?.(b.target)}>
                <SettingsEthernetRoundedIcon fontSize = "small" sx = {{ color: b.capacity ? 'primary.main' : 'text.disabled'}} />
              </IconButton>
            </Tooltip>
          )}
          {canSuppress && (b.suppressed
            ? (
              <Tooltip title="Remove suppression">
                <IconButton size="small" onClick={() => onUnsuppress?.(b.target)}>
                  <UndoRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
            : (
              <Tooltip title="Suppress this line item">
                <IconButton size="small" onClick={() => onSuppress?.(b.target)}>
                  <BlockRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </IconButton>
              </Tooltip>
            ))}
        </TableCell>
      </TableRow>
      {rawEntries.length > 0 && (
        <TableRow>
          <TableCell sx={{ py: 0 }} colSpan={5}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ py: 1, pl: 4 }}>
                {rawEntries.map(([k, v]) => (
                  <Stack key={k} direction="row" spacing={1} sx={{ py: 0.25 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>{k}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{fmtRawVal(k, v)}</Typography>
                  </Stack>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Right-side slide-out showing the raw data behind one Technical Health check so
 * an admin can verify both positive and negative results:
 *   - the resolved evaluation (status, message, gap, override),
 *   - a per-record breakdown (one row per audit doc — e.g. per device), and
 *   - the raw JSON of the underlying records (audit docs or the Knowledge entry).
 *
 * Props:
 *   open, onClose
 *   check        — the registry check
 *   evaluation   — evals[check.key]
 *   records      — raw cma.audit docs for check.source.auditRunId (audit checks)
 *   manualRecord — Knowledge manual directive (manual checks), or null
 *   override     — Knowledge override directive, or null
 */
export default function CheckDetailDrawer({ open, onClose, check, evaluation, records = [], manualRecord, override, suppressTargets, onSuppressTarget, onUnsuppressTarget, circuitCapacityTargets, onSetCircuitCapacity, onRequeue, requeueBusy, canRequeue }) {
  const theme = useTheme();
  const [showJson, setShowJson] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [copied, setCopied] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [query, setQuery] = useState('');

  // The Drawer stays mounted when closed, so this state would otherwise persist into
  // the next check you open - a 'fail' filter carried onto an all-passing check renders
  // as empty Results lis that looks like missing data. Reset per open and per check.

  useEffect(() =>{
    setStatusFilter(null);
    setQuery('');
    setShowJson(false);
    setShowDesc(false);
  }, [open, check?.key]);

  const description = check ? CHECK_DESCRIPTIONS[check.key] : null;
  const checkStandards = check ? standardsForCheck(check.key) : [];

  const isAudit = check?.source?.type === 'audit';
  const isPending = check?.source?.type === 'pending';
  const checkKey = check?.source?.checkKey;

  // Capability isn't in the customer's plan (tier too low / add-on not held, and
  // no manual value or vCIO override opting it back in). evaluateCheck flags this
  // as a service gap. We must NOT surface the underlying audit results or raw
  // data for a service we don't provide them — show the gap, not the health data.
  const outOfPlan = !!evaluation?.serviceGap;

  // Whether a vCIO can suppress individual line items from this blade. Suppression
  // targets per-target audit rows; we never offer it for out-of-plan checks (whose
  // data is hidden) or non-audit checks.
  const canSuppress = isAudit && !outOfPlan && typeof onSuppressTarget === 'function';

  //Only the ISP -Bandwidth check carries per-circuit contracted-capacity rows.
  const canSetCapacity = isAudit && !outOfPlan && checkKey === 'ispBandwidth' && typeof onSetCircuitCapacity === 'function';

  // Build the per-target breakdown. Preferred granularity is the check's own
  // `details` array (one entry per device/item the check examined — e.g. each
  // firewall). When a check carries no details we fall back to one row per audit
  // doc, which is how the per-device record audits (Windows endpoint) already
  // surface granularity. Account-level checks with neither show their rollup.
  const breakdown = useMemo(() => {
    if (!isAudit) return [];
    const isSuppressed = (t) => !!suppressTargets && suppressTargets.has(String(t));
    const capOf = (t) => (circuitCapacityTargets ? circuitCapacityTargets.get(String(t)):undefined);
    const rows = [];
    for (const r of records) {
      const c = checkKey && r.jobResults && r.jobResults.checks ? r.jobResults.checks[checkKey] : null;
      if (c && Array.isArray(c.details) && c.details.length) {
        for (const d of c.details) {
          rows.push({ id: `${r.id}:${d.target}`, target: d.target, date: r.auditDate, result: d.result, message: d.message, raw: d, suppressed: isSuppressed(d.target), capacity : capOf(d.target) });
        }
      } else if (checkKey) {
        // Per-device audits emit different check keys per device type (e.g. a
        // desktop carries patchEndpoint, a server patchServer). Skip records that
        // don't carry this check so the breakdown lists only the relevant targets —
        // matching the X/Y tally from statusFromAudit.
        if (!c) continue;
        const t = r.title || r.account || r.id;
        rows.push({ id: r.id, target: t, date: r.auditDate, result: c.result, message: c.message, raw: c, suppressed: isSuppressed(t), capacity : capOf(t) });
      } else {
        const t = r.title || r.account || r.id;
        rows.push({ id: r.id, target: t, date: r.auditDate, result: r.answer, message: r.comments || null, raw: { answer: r.answer, comments: r.comments || '' }, suppressed: isSuppressed(t), capacity : capOf(t) });
      }
    }
    rows.sort((a, b) =>
      String(a.target).localeCompare(String(b.target), undefined, { numeric: true, sensitivity: 'base' }),
    );
    return rows;
  }, [records, checkKey, isAudit, suppressTargets, circuitCapacityTargets]);


  // Fall back to the unfiltered list wen the active filter matcher nothing: the status
  // chips only render while their tally is > 0, so a filter whose bucket has emptied
  // (eg. after a requeue clears the failures) leaves no chip to click to clear it.

  const filteredBreakdown = useMemo(() => {
    // Text filter first, and NOT subject to the fall-back-when-empty rule below: an
    // empty search result must stay empty, or typing a typo would silently show all
    // 1500 rows back and read as a match.
    const q = query.trim().toLowerCase();
    const searched = q
      ? breakdown.filter(b =>
          `${b.target ?? ''} ${b.message ?? ''} ${b.result ?? ''}`.toLowerCase().includes(q))
      : breakdown;
    if (!statusFilter) return searched;
    const f = searched.filter(b => rowBucket(b) === statusFilter);
    return f.length ? f : searched;
  }, [breakdown, statusFilter, query]);

  const tally = useMemo(() => {
    const t = { pass: 0, warn: 0, fail: 0, other: 0, suppressed: 0 };
    for (const b of breakdown) {
      if (b.suppressed) { t.suppressed += 1; continue; }
      const R = (b.result == null ? '' : String(b.result)).trim().toUpperCase();
      if (R === 'PASS') t.pass += 1;
      else if (R === 'WARN' || R === 'WARNING') t.warn += 1;
      else if (R === 'FAIL') t.fail += 1;
      else t.other += 1;
    }
    return t;
  }, [breakdown]);

  const rawPayload = useMemo(() => {
    if (isAudit) return records;
    return [manualRecord, override].filter(Boolean);
  }, [isAudit, records, manualRecord, override]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(rawPayload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — ignore */ }
  };

  if (!check) return null;
  const sIcon = STATUS_ICON[evaluation?.status] || STATUS_ICON.nodata;
  const SIcon = sIcon.Icon;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100%' } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <SIcon sx={{ color: sIcon.color, fontSize: 22 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{check.label}</Typography>
          {check.subcategory && <Typography variant="caption" color="text.secondary">{check.subcategory}</Typography>}
        </Box>
        {evaluation?.gap && (
          <Chip size="small" variant="outlined" color={evaluation.gap.color} label={evaluation.gap.label} />
        )}
        <IconButton size="small" onClick={onClose}><CloseRoundedIcon /></IconButton>
      </Stack>

      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {/* Description (collapsed by default) — what the check verifies, plus the
            compliance standards (beyond the CMA baseline) it contributes to. */}
        {description && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setShowDesc(v => !v)}>
              {showDesc ? <KeyboardArrowDownRoundedIcon fontSize="small" /> : <KeyboardArrowRightRoundedIcon fontSize="small" />}
              <Typography variant="overline" color="text.secondary">Description</Typography>
            </Stack>
            <Collapse in={showDesc}>
              <Box sx={{ pl: 1, pt: 0.5 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{description}</Typography>
                {checkStandards.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Compliance standards
                    </Typography>
                    <Stack spacing={0.5}>
                      {checkStandards.map(s => (
                        <Stack key={s.standard} direction="row" alignItems="flex-start" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip size="small" color="info" variant="outlined" label={s.standard} sx={{ height: 20 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', pt: 0.25 }}>
                            {s.refs.join(' · ')}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Resolved evaluation */}
        <Typography variant="overline" color="text.secondary">Evaluation</Typography>
        <Box sx={{ mb: 2 }}>
          <Row label="Status">{evaluation?.status}</Row>
          {evaluation?.value && <Row label="Value">{evaluation.value}</Row>}
          {evaluation?.message && <Row label="Message">{evaluation.message}</Row>}
          <Row label="Source">{isAudit ? `audit · ${check.source.auditRunId}${checkKey ? ` · ${checkKey}` : ''}` : isPending ? 'automated (producer not live yet)' : 'manual / Knowledge'}</Row>
          <Row label="Required tier">{check.tier || '—'}{check.addon ? ` (add-on: ${check.addon})` : ''}</Row>
          {evaluation?.overridden && (
            <Row label="Override">
              {`${override?.value || ''} — ${override?.reason || ''}${override?.override_expires ? ` (until ${String(override.override_expires).slice(0, 10)})` : ''} · by ${override?.recorded_by || 'vCIO'}`}
            </Row>
          )}
        </Box>

        {/* Out-of-plan: hide all health data; show the gap instead. */}
        {outOfPlan && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, border: `1px dashed ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.warning.main, 0.06) }}>
            <Typography variant="overline" color="text.secondary">Not in plan</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {`This capability isn’t included in the customer’s plan${
                check.addon ? ` (add-on: ${check.addon})` : check.tier ? ` (requires ${check.tier})` : ''
              }. Its audit results are hidden until the entitlement or required tier is in place — or a vCIO records a manual value.`}
            </Typography>
            {/* A stored override on an out-of-plan check is kept but not honoured:
                the plan outranks it. Say so, or the vCIO who recorded it concludes
                it never saved. */}
            {evaluation?.suppressedOverride && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {`A vCIO override is on file (${evaluation.suppressedOverride.value}${
                  evaluation.suppressedOverride.reason ? ` — ${evaluation.suppressedOverride.reason}` : ''
                } · by ${evaluation.suppressedOverride.recorded_by || 'vCIO'}) but is not applied, because the plan takes precedence. It will take effect if the entitlement or required tier is added.`}
              </Typography>
            )}
          </Box>
        )}

        {/* Producer for this check isn't live yet — explain, don't show health data. */}
        {!outOfPlan && isPending && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, border: `1px dashed ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, 0.04) }}>
            <Typography variant="overline" color="text.secondary">Automated check pending</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {check.source.note || 'The automated producer that will emit this check isn’t live yet. A vCIO can still record a manual value or override in the meantime.'}
            </Typography>
          </Box>
        )}

        {/* Per-record breakdown (audit checks) */}
        {!outOfPlan && isAudit && (
          <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="overline" color="text.secondary">
                Results ({breakdown.length})
              </Typography>
              <Box sx={{ flex: 1 }} />
              {tally.pass > 0 && <Chip size="small" color="success" variant={statusFilter === 'pass' ? 'filled' : 'outlined'} label={`${tally.pass} pass`} onClick={() => setStatusFilter((f) => f === 'pass' ? null : 'pass')} sx={{ height: 18, cursor: 'pointer' }} />}
              {tally.warn > 0 && <Chip size="small" color="warning" variant={statusFilter === 'warn' ? 'filled' : 'outlined'} label={`${tally.warn} warn`} onClick={() => setStatusFilter((f) => f === 'warn' ? null : 'warn')} sx={{ height: 18, cursor: 'pointer' }} />}
              {tally.fail > 0 && <Chip size="small" color="error"   variant={statusFilter === 'fail' ? 'filled' : 'outlined'} label={`${tally.fail} fail`} onClick={() => setStatusFilter((f) => f === 'fail' ? null : 'fail')} sx={{ height: 18, cursor: 'pointer' }} />}
              {tally.suppressed > 0 && <Chip size="small" variant={statusFilter === 'suppressed' ? 'filled' : 'outlined'} label={`${tally.suppressed} suppressed`} onClick={() => setStatusFilter((f) => f === 'suppressed' ? null : 'suppressed')} sx={{ height: 18, cursor: 'pointer' }} />}
              <Tooltip title={canRequeue? (['DATTO','LANSWEEPER'].includes(check.source.auditRunId) ? 'Re-run this producer for the whole account' : 'Re-run this check now') : 'Re-run is only available for today’s automated results'}>
                <span>
                  <Button size="small" startIcon={requeueBusy ? <CircularProgress size={14} /> : <ReplayRoundedIcon />}
                    disabled={!canRequeue || requeueBusy} onClick={onRequeue}>
                    {['DATTO','LANSWEEPER'].includes(check.source.auditRunId) ? 'Re-run (account)' : 'Re-run'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            {/* A tenant-level check on a parent account can carry well over a thousand
                per-user rows (ASL: ~1500), which is unusable when the reason you opened
                this blade is to suppress ONE of them. Shown only once the list is long
                enough to need it. */}
            {breakdown.length > 12 && (
              <TextField
                size="small"
                fullWidth
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Filter ${breakdown.length} results by target, message or result`}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setQuery('')} aria-label="clear filter">
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
            {query.trim() && filteredBreakdown.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                No result matches &ldquo;{query.trim()}&rdquo;.
              </Typography>
            )}
            {breakdown.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                No audit records for {check.source.auditRunId} on this customer yet.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 2, '& td, & th': { px: 1, py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 28, p: 0 }} />
                    <TableCell>Target</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Detail</TableCell>
                    <TableCell sx={{ width: 36, p: 0 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBreakdown.map((b) => (
                    <ResultRow key={b.id} b={b} canSuppress={canSuppress}
                      onSuppress={onSuppressTarget} onUnsuppress={onUnsuppressTarget}
                      canSetCapacity={canSetCapacity} onSetCapacity={onSetCircuitCapacity}/>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}

        {/* Raw JSON — hidden for out-of-plan checks (don't expose the data). */}
        {!outOfPlan && (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button size="small" onClick={() => setShowJson(v => !v)}>{showJson ? 'Hide' : 'Show'} raw JSON</Button>
              <Box sx={{ flex: 1 }} />
              <Tooltip title={copied ? 'Copied' : 'Copy raw JSON'}>
                <span>
                  <IconButton size="small" onClick={copyJson} disabled={!rawPayload.length}><ContentCopyRoundedIcon fontSize="small" /></IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Collapse in={showJson}>
              <Box component="pre" sx={{
                mt: 1, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.04),
                fontSize: 11, lineHeight: 1.5, overflowX: 'auto', maxHeight: '40vh',
                border: `1px solid ${theme.palette.divider}`,
              }}>
                {rawPayload.length ? JSON.stringify(rawPayload, null, 2) : 'No raw records.'}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Drawer>
  );
}
